import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useAuthn } from '../hooks/useAuthn';
import { type User } from '../types';

type AuthenticationContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  requiresAuthentication: boolean;
  authenticateUser: () => Promise<User>;
  user: User;
  signOut: () => Promise<void>;
};

let getAuthUserGlobal: (() => Promise<User>) | null = null;
let userGlobal: User | null = null;

/**
 * WARNING: this function is a horrible hack to get a user without using a hook, e.g. within rtk-query context
 * or for axios interceptors. It should *not* be used unless absolutely necessary.
 *
 * Authenticate and return the user.
 * If called before initialization, it will throw an error.
 *
 * @returns {User} The user object.
 * @throws {Error} If the AuthenticationProvider has not been initialized.
 */
export const authenticateUser = async (): Promise<User> => {
  if (getAuthUserGlobal === null) {
    throw new Error('Ensure AuthenticationProvider has been initialized');
  }

  return getAuthUserGlobal();
};

/**
 * WARNING: this function is a horrible hack to get a user without using a hook, e.g. within rtk-query context
 * or for axios interceptors. It should *not* be used unless absolutely necessary.
 *
 * Retrieves the user object.
 * If called before initialization, it will throw an error.
 *
 * @returns {User} The user object.
 * @throws {Error} If the AuthenticationProvider has not been initialized.
 */
export const getUser = (): User => {
  if (userGlobal === null) {
    throw new Error('Ensure AuthenticationProvider has been initialized');
  }

  return userGlobal;
};

const AuthenticationContext = createContext<
  AuthenticationContextValue | undefined
>(undefined);

export const AuthenticationProvider = ({
  children,
  requiresAuthentication = true,
}: {
  children: ReactNode;
  requiresAuthentication?: boolean;
}) => {
  const authContext = useAuthn(requiresAuthentication);

  getAuthUserGlobal = authContext.authenticateUser;
  userGlobal = authContext.user;

  const contextValue = useMemo(
    () => ({
      ...authContext,
      requiresAuthentication,
    }),
    [authContext, requiresAuthentication],
  );

  return (
    <AuthenticationContext.Provider value={contextValue}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthentication = () => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error(
      'useAuthentication must be used within an AuthenticationProvider',
    );
  }
  return context;
};
